import { memo, useState } from "react";
import { NavLink } from "react-router-dom";

import { usePaymentsContext, useRegistrationContext } from "@zenith/context";
import { useFinishRegistrationModal } from "@zenith/features/Dashboard";
import {
  PaymentMethodsButtons,
  PaymentMethodsEnum,
} from "@zenith/features/Payments/types";
import {
  COLLECT_PAYMENT,
  DASHBOARD,
  MAKE_PAYMENT,
  PAYMENT,
} from "@zenith/routes/paths";

export interface PaymentMethodButtonsProps {
  btnHeight: string;
  btnWidth: string;
  bg: string;
  dashboard: boolean;
  selectedPaymentMethod?: PaymentMethodsEnum | null;
  onSelectPaymentMethod?: (method: PaymentMethodsEnum | null) => void;
}

const PaymentMethodButtons = memo(function PaymentMethodButtons(
  props: PaymentMethodButtonsProps
) {
  const {
    bg,
    btnHeight,
    btnWidth,
    dashboard,
    selectedPaymentMethod: externalSelectedMethod,
    onSelectPaymentMethod,
  } = props;

  const { openFinishRegistrationModal, finishRegistrationModal } =
    useFinishRegistrationModal();
  const { registrationContext } = useRegistrationContext();
  const { paymentsContext, setPaymentsContext } = usePaymentsContext();

  const [selectedButton, setSelectedButton] = useState<
    PaymentMethodsEnum | undefined
  >(externalSelectedMethod ?? paymentsContext.paymentMethod);

  const handlePayment = (paymentButton: PaymentMethodsEnum) => {
    setSelectedButton(paymentButton);
    onSelectPaymentMethod?.(paymentButton);
    setPaymentsContext({ ...paymentsContext, paymentMethod: paymentButton });
  };

  const renderButton = (
    type: string,
    button: { text: string; icon: React.FC<React.SVGProps<SVGSVGElement>> },
    index: number,
    onClick?: () => void,
    additionalClasses?: string
  ) => {
    const isSelected = selectedButton === index;
    const Icon = button.icon;

    const commonButtonProps = {
      style: {
        width: `${btnWidth}rem`,
        height: `${btnHeight}rem`,
      },
      className: `rounded-1.25l p-4 text-sm transition-colors duration-300 ease-in-out group-hover:bg-primary-300 ${
        isSelected ? "bg-primary-300" : `bg-${bg}`
      } ${additionalClasses || ""}`,
    };

    return (
      <button
        id={`${button.text.replace(/\s/g, "-")}-${
          type === "payment" ? "btn" : "dashboard-btn"
        }`}
        {...commonButtonProps}
        onClick={onClick}
      >
        <Icon
          className={`transition-colors duration-300 ease-in-out group-hover:fill-white ${
            isSelected ? "fill-white" : ""
          }`}
          style={{ transform: "scale(1)" }}
        />
        {type === "dashboard" && (
          <p
            className={`flex text-base transition-colors duration-300 ease-in-out group-hover:text-white ${
              isSelected ? "text-white" : ""
            }`}
          >
            {button.text}
          </p>
        )}
      </button>
    );
  };

  const dashBoardButton = (
    button: { text: string; icon: React.FC<React.SVGProps<SVGSVGElement>> },
    index: number
  ) =>
    renderButton(
      "dashboard",
      button,
      index,
      () => handlePayment(index),
      "flex flex-col justify-between"
    );

  const paymentMethodButton = (
    button: { text: string; icon: React.FC<React.SVGProps<SVGSVGElement>> },
    index: number
  ) =>
    renderButton(
      "payment",
      button,
      index,
      () => handlePayment(index),
      "flex items-center justify-center"
    );

  return (
    <div
      className={`flex flex-wrap justify-between ${
        dashboard ? "gap-5" : "gap-1"
      }`}
    >
      {PaymentMethodsButtons.map((button) => {
        if (button === null) return [];
        // Static account checkout only exists in the Collect Payment cart flow.
        if (button.index === PaymentMethodsEnum.STATIC_ACCOUNT) return [];
        const isSelected = selectedButton === button.index;

        return (
          <div key={button.index} className="button-group">
            {dashboard ? (
              <NavLink
                to={
                  registrationContext.isRegistrationCompleted
                    ? dashboard
                      ? COLLECT_PAYMENT
                      : MAKE_PAYMENT
                    : DASHBOARD
                }
                onClick={() => {
                  if (!registrationContext.isRegistrationCompleted) {
                    openFinishRegistrationModal();
                  }
                }}
                style={{ textDecoration: "none" }}
              >
                {dashBoardButton(button, button.index)}
              </NavLink>
            ) : (
              <>
                <NavLink
                  to={
                    registrationContext.isRegistrationCompleted
                      ? MAKE_PAYMENT
                      : DASHBOARD
                  }
                  onClick={() => {
                    if (!registrationContext.isRegistrationCompleted) {
                      openFinishRegistrationModal();
                    }
                  }}
                  style={{ textDecoration: "none" }}
                >
                  {paymentMethodButton(button, button.index)}
                </NavLink>
                <p
                  className={`text-xs ${
                    isSelected ? "text-red-600" : ""
                  } flex justify-center pt-2 pb-8`}
                >
                  {button.text}
                </p>
              </>
            )}
          </div>
        );
      })}
      {finishRegistrationModal}
    </div>
  );
});

export default PaymentMethodButtons;
